/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import { toKebabCase } from "src/utilities/utils.js";
import QuizConfirmAnswers from "../../QuizConfirmAnswers/jsx/index.jsx";
import { useQuiz } from "src/contexts/QuizContext.js";
import { quizInteractionTracking } from "src/modules/QuizHelpers/index.js";

function QuizQuestionRange({
  questionKey,
  sectionKey,
  rangeQuestionName,
  rangeLabelTop,
  rangeLabelBottom,
  rangeEmojis,
  question,
}) {
  const { dispatch, windowWidth } = useQuiz();
  const section = question.subquestions[sectionKey];
  const allOptions = [...section.options].reverse();
  const optionsLength = parseInt(section.options.length);

  let defaultRangeValue = -1;
  const rangeAnswer = section.currentAnswer ?? section.savedAnswer;
  if (rangeAnswer) {
    const index = allOptions.indexOf(rangeAnswer);
    defaultRangeValue = index + 1;
  }

  const [rangeValue, setRangeValue] = useState(defaultRangeValue);
  const [currentAnswer, setCurrentAnswer] = useState(section.currentAnswer);

  // handle option click, add small delay for mobile devices to ensure user know's selection has been made
  const handleButton = (e, optionIndex, option) => {

    e.preventDefault();
    // Comprehensive focus reset for mobile devices
    e.target.blur();
    if (document.activeElement && document.activeElement.blur) {
      document.activeElement.blur();
    }
    document.body.focus();
    document.body.blur();

    // Get question number from questionKey (e.g., "question1" -> "Q1")
    const questionNumber = questionKey.replace("question", "Q");

    // Get sub question index from sectionKey (e.g., "section1" -> 1)
    const subQuestionIndex = sectionKey.replace("section", "");
    const subQuestionIndexNum = parseInt(subQuestionIndex, 10) || null;

    // Get selected answer index (1-based)
    const selectedAnswerIndex = optionIndex + 1;

    // Check if answer changed (had a previous answer)
    // Use savedAnswer first (persisted), then currentAnswer (temporary)
    // Only true if there was a previous answer AND it's different from current selection
    const previousAnswer = section.savedAnswer || section.currentAnswer;
    const answerChanged = previousAnswer ? previousAnswer !== option : false;

    // Determine if this is a sub-question (multiple sections) or main question
    const isSubQuestion = Object.keys(question.subquestions).length > 1;


     


    // Track option selection (same as QuizQuestionOptions)
    quizInteractionTracking({
      interaction_type: "select",
      question_number: questionNumber,
      sub_question_index: isSubQuestion ? subQuestionIndexNum : null,
      selected_answer_index: selectedAnswerIndex,
      answer_changed: answerChanged,
      navigation_action: null,
    });
    
    setRangeValue(optionIndex + 1);
    const type = windowWidth < 1024 ? "saveAnswer" : "currentAnswer";
    const delay = windowWidth < 1024 ? 500 : 0;
    setTimeout(() => {
      dispatch({
        type,
        payload: {
          questionKey: questionKey,
          sectionKey: sectionKey,
          value: option,
        },
      });
      if (delay)
        dispatch({
          type: "qContainerScroll",
        });
    }, delay);
  };

  const props = {
    rangeQuestionName,
    rangeValue,
    optionsLength,
    allOptions,
    setRangeValue,
    setCurrentAnswer,
    currentAnswer,
    questionKey,
    sectionKey,
    question,
    payload: {
      questionKey,
      sectionKey,
      value: currentAnswer,
    },
  };

  useEffect(() => {
    const handler = () => {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    };
    document.addEventListener("mouseup", handler);
    return () => document.removeEventListener("mouseup", handler);
  }, []);

  return question.status == "start" || question.status == "progress" ? (
    <>
      <div className="quiz-question__range">
        {section.title ? (
          <label
            htmlFor={rangeQuestionName}
            className="quiz-question__range-label"
            id={rangeQuestionName}
          >
            {section.title}
          </label>
        ) : (
          ``
        )}
        {rangeLabelTop ? (
          <span className="quiz-question__range-legend legend--max">
            {rangeLabelTop}
          </span>
        ) : (
          ``
        )}

        <div className="quiz-question__range-wrapper">
          <span className="quiz-question__range-extend-top"></span>
          <RangeInput {...props} />
          <span className="quiz-question__range-extend-bottom"></span>

          <div
            className={`quiz-question__range-buttons range--${optionsLength}`}
          >
            <div className="quiz-question__buttons-marker">
              {allOptions.map((option, optionIndex) => (
                <button
                  className={`${option === currentAnswer ? "selected" : ""}`}
                  key={option}
                  value={option}
                  aria-label={`${toKebabCase(`${rangeQuestionName}-${option}`)}`}
                  onClick={(e) => {
                    handleButton(e, optionIndex, option);
                    console.log({e, optionIndex, option});
                  }}
                >
                  {rangeEmojis ? (
                    <span
                      className={`quiz-question__buttons-emoji emoji--${optionIndex}`}
                    ></span>
                  ) : (
                    ``
                  )}
                  <span className="quiz-question__buttons-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
          {rangeLabelBottom ? (
            <span className="quiz-question__range-legend legend--min">
              {rangeLabelBottom}
            </span>
          ) : (
            ``
          )}
        </div>
      </div>
    </>
  ) : (
    windowWidth > 1024 && <QuizConfirmAnswers _question={question} />
  );
}

export const RangeInput = ({
  rangeQuestionName,
  rangeValue,
  optionsLength,
  setCurrentAnswer,
  setRangeValue,
  allOptions,
  currentAnswer,
  questionKey,
  sectionKey,
  question,
  payload,
}) => {
  // Max Value of range slider set by rangeLength
  const { dispatch } = useQuiz();

  // handle range click (desktop)
  const handleChange = (event) => {
    const inputRangeValue = event.target.value;
    setRangeValue(inputRangeValue);
    setCurrentAnswer(allOptions[inputRangeValue - 1]);
  };

  const handleMouseUp = (e) => {
    // Comprehensive focus reset for mobile devices
    e.target.blur();
    if (document.activeElement && document.activeElement.blur) {
      document.activeElement.blur();
    }
    document.body.focus();
    document.body.blur();

    // Get question number from questionKey (e.g., "question1" -> "Q1")
    const questionNumber = questionKey.replace("question", "Q");

    // Get sub question index from sectionKey (e.g., "section1" -> 1)
    const subQuestionIndex = sectionKey.replace("section", "");
    const subQuestionIndexNum = parseInt(subQuestionIndex, 10) || null;

    // Get selected answer index (1-based, rangeValue is already 1-based)
    const selectedAnswerIndex = rangeValue;

    // Get section for checking previous answer
    const section = question?.subquestions?.[sectionKey];
    // Use savedAnswer first (persisted), then currentAnswer (temporary)
    const previousAnswer = section?.savedAnswer || section?.currentAnswer;
    const answerChanged = previousAnswer ? previousAnswer !== currentAnswer : false;

    // Determine if this is a sub-question (multiple sections) or main question
    const isSubQuestion = question ? Object.keys(question.subquestions).length > 1 : false;

    // Track option selection (same as QuizQuestionOptions)
    quizInteractionTracking({
      interaction_type: "select",
      question_number: questionNumber,
      sub_question_index: isSubQuestion ? subQuestionIndexNum : null,
      selected_answer_index: selectedAnswerIndex,
      answer_changed: answerChanged,
      navigation_action: null,
    });
    
    dispatch({
      type: "currentAnswer",
      payload,
    });
  };

  const progress = ((rangeValue - 1) / (optionsLength - 1)) * 100;
  const rangeStyle = {
    background: `linear-gradient(to top, #cbe0ff ${progress}%, #fff ${progress}%)`,
    borderLeft: `1px #dfdfdf solid`,
    borderRight: `1px #dfdfdf solid`,
  };

  useEffect(() => {
    const handler = () => {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    };
    document.addEventListener("mouseup", handler);
    return () => document.removeEventListener("mouseup", handler);
  }, []);

  return (
    <>
      <input
        name={rangeQuestionName}
        type="range"
        role="slider"
        list="markers"
        className="quiz-question__range-input"
        // Needed for Firefox
        orient="vertical"
        value={rangeValue}
        onChange={handleChange}
        onMouseUp={handleMouseUp}
        min={1}
        max={optionsLength}
        aria-orientation="vertical"
        aria-valuenow={rangeValue}
        aria-valuemin={1}
        aria-valuemax={optionsLength}
        style={rangeStyle}
        aria-controls={`${toKebabCase(rangeQuestionName)}-slider`}
        aria-labelledby={rangeQuestionName}
        tabIndex="0"
        step={1}
      />
    </>
  );
};

export default QuizQuestionRange;
